/// <amd-module name="Build/Pipelines/Tabs/Pipelines/PipelinesTreeItemProvider" />
import { BuildStatus, Folder } from "Build/Client/Contracts/Build";
import { FolderData, PipelineData, PipelineDataList, RunData } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { atou } from "Build/Common/Library/String";
import { ContributionSource } from "Build/Flux/Sources/Contribution";
import { BuildSignalRActionHub, IBuildUpdatedPayload, IStagesUpdatedPayload } from "Build/Pipelines/Actions/SignalR";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { ILinkProps } from "VSSUI/Link";
import { ITreeItem, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";

export interface IPipelineTreeItem {
    name: string;
    path: string;
    isLoading: boolean;
    isEmptyMarker: boolean;
    pipeline?: PipelineData;
    folder?: FolderData;
    linkProps?: ILinkProps;
}

enum LoadingStatus {
    NotRequested = 0,
    Requested = 1,
    Available = 2
}

const c_batchingDelayInMs = 100;
const c_signalRBatchingDelayInMs = 3 * 1000;
const c_forwardBatchSize = 20;

export class PipelineTreeItemProvider extends TreeItemProvider<ObservableValue<IPipelineTreeItem>> {
    private _rootPath: string;
    private _pageContext: IVssPageContext;
    private _paths: { [key: string]: LoadingStatus };
    private _pathMapping: { [key: string]: string };
    private _source: ContributionSource;
    private _loadingRoot: ObservableValue<boolean>;
    private _expandedFolders: string[];
    private _detailRequests: { [key: number]: LoadingStatus };
    private _timerManagement: TimerManagement;
    private _batchForUpdates: number[];
    private _fetchDetailsDebounced: () => void;
    private _signalRHub: BuildSignalRActionHub;
    private _runsWithUpdates: number[];
    private _getUpdatedRunsDebounced: () => void;
    private _filteredProvider: FilteredPipelineTreeItemProvider | undefined;

    constructor(
        pageContext: IVssPageContext,
        signalRHub: BuildSignalRActionHub,
        isPageLoad: boolean,
        isInitialDataFiltered: boolean,
        rootPath: string,
        treeState: string
    ) {
        super([]);

        this._rootPath = rootPath;

        this._pageContext = pageContext;
        this._paths = {};
        this._pathMapping = {};
        this._detailRequests = {};
        this._loadingRoot = new ObservableValue<boolean>(false);
        this._expandedFolders = treeState ? atou(treeState).split("$") : [];

        this._timerManagement = new TimerManagement();
        this._fetchDetailsDebounced = this._timerManagement.debounce(this._fetchDetails, c_batchingDelayInMs);
        this._batchForUpdates = [];

        this._signalRHub = signalRHub;
        this._runsWithUpdates = [];
        this._getUpdatedRunsDebounced = this._timerManagement.debounce(this._getUpdatedRuns, c_signalRBatchingDelayInMs);
        this._signalRHub.buildUpdated.addListener(this._buildUpdated);
        this._signalRHub.stagesUpdated.addListener(this._updateStages);

        this._source = new ContributionSource({
            pageContext: this._pageContext
        });

        if (isPageLoad && !isInitialDataFiltered) {
            this._getChildItemsOnPageLoad();
        }
    }

    public loadRoot() {
        if (!this._hasRequestedItemsAtPath(this._rootPath)) {
            this._loadingRoot.value = true;
            this._getChildItems();
        }
    }

    get loadingRoot(): IObservableValue<boolean> {
        return this._loadingRoot;
    }

    public toggle(treeItem: ITreeItem<ObservableValue<IPipelineTreeItem>>): void {
        super.toggle(treeItem);

        if (treeItem.expanded) {
            this._expandedFolders.push(treeItem.data.value.path);
        } else {
            const folderPath = treeItem.data.value.path;
            const folderIndex = this._expandedFolders.indexOf(folderPath);
            if (folderIndex > -1) {
                this._expandedFolders.splice(folderIndex, 1);
            }
        }

        if (treeItem.expanded && !this._hasRequestedItemsAtPath(treeItem.data.value.path)) {
            this._getChildItems(treeItem);
        }
    }

    public fetchDetails(definitionId: number, pipelineIndex: number) {
        const requestStatus = this._detailRequests[definitionId];
        if (requestStatus === undefined || requestStatus === LoadingStatus.NotRequested) {
            // Include next set of pipelines in addition to this one in the request
            for (let j = pipelineIndex; j < this.value.length && j < pipelineIndex + c_forwardBatchSize; j++) {
                const pipeline = this.value[j].underlyingItem.data.value.pipeline;
                if (pipeline) {
                    if (this._detailRequests[pipeline.id] === undefined || this._detailRequests[pipeline.id] === LoadingStatus.NotRequested) {
                        this._detailRequests[pipeline.id] = LoadingStatus.Requested;
                        this._batchForUpdates.push(pipeline.id);
                    }
                }
            }
            if (this._batchForUpdates.length > 0) {
                this._fetchDetailsDebounced();
            }
        }
    }

    public hasDetails(definitionId: number): boolean {
        if (this._detailRequests[definitionId] === LoadingStatus.Available) {
            return true;
        } else {
            return false;
        }
    }

    public get expandedFolders(): string {
        return this._expandedFolders.join("$");
    }

    public folderRenamed(oldPath: string, newPath: string) {
        const folderIndex = this._expandedFolders.indexOf(oldPath);
        if (folderIndex > -1) {
            this._expandedFolders.splice(folderIndex, 1);
            this._expandedFolders.push(newPath);
        }

        this.value.forEach((item: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
            const itemValue = item.underlyingItem.data.value;
            if (itemValue.path === oldPath) {
                let newVersion: IPipelineTreeItem = {
                    name: itemValue.name,
                    path: newPath,
                    isLoading: itemValue.isLoading,
                    isEmptyMarker: itemValue.isEmptyMarker
                };
                if (itemValue.folder) {
                    const linkingService = this._pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
                    const newFolderUrl = linkingService.getPipelinesFolderUrl(undefined, undefined, newPath);
                    newVersion.name = newPath.split("\\").reverse()[0];
                    newVersion.folder = itemValue.folder;
                    newVersion.folder.path = newPath;
                    newVersion.folder.linkProps.href = newFolderUrl;
                } else if (itemValue.pipeline) {
                    newVersion.pipeline = itemValue.pipeline;
                    newVersion.pipeline.path = newPath;
                }

                item.underlyingItem.data.value = newVersion;

                this._addPathMapping(oldPath, newPath);
            } else if (itemValue.path.startsWith(oldPath + "\\")) {
                let updatedPath = itemValue.path.replace(oldPath + "\\", newPath + "\\");
                let newVersion: IPipelineTreeItem = {
                    name: itemValue.name,
                    path: updatedPath,
                    isLoading: itemValue.isLoading,
                    isEmptyMarker: itemValue.isEmptyMarker
                };
                if (itemValue.folder) {
                    const linkingService = this._pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
                    const newFolderUrl = linkingService.getPipelinesFolderUrl(undefined, undefined, updatedPath);
                    newVersion.folder = itemValue.folder;
                    newVersion.folder.path = updatedPath;
                    newVersion.folder.linkProps.href = newFolderUrl;
                } else if (itemValue.pipeline) {
                    newVersion.pipeline = itemValue.pipeline;
                    newVersion.pipeline.path = updatedPath;
                }

                item.underlyingItem.data.value = newVersion;

                this._addPathMapping(itemValue.path, updatedPath);
            }
        });
    }

    public folderCreated(newFolder: Folder) {
        const parentPath = newFolder.path.substr(0, newFolder.path.lastIndexOf("\\"));
        const newFolderName = newFolder.path.split("\\").reverse()[0];

        const linkingService = this._pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const folderUrl = linkingService.getPipelinesFolderUrl(undefined, undefined, newFolder.path);

        const newItem: ITreeItem<ObservableValue<IPipelineTreeItem>> = {
            data: new ObservableValue<IPipelineTreeItem>({
                name: newFolderName,
                path: newFolder.path,
                isLoading: false,
                isEmptyMarker: false,
                folder: { path: newFolder.path, name: newFolderName, linkProps: { href: folderUrl } } as FolderData
            }),
            childItems: []
        };

        if ((this._rootPath === "\\" && parentPath === "") || parentPath === this._rootPath) {
            this._addPathMapping(this._rootPath, newFolder.path);
            this.splice(undefined, undefined, [{ items: [newItem] }]);
            return;
        } else {
            this._addPathMapping(parentPath, newFolder.path);
            for (let i = 0; i < this.value.length; i++) {
                if (this.value[i].underlyingItem.data.value.folder && this.value[i].underlyingItem.data.value.path === parentPath) {
                    const parentItem = this.value[i].underlyingItem;
                    if (parentItem.expanded) {
                        this._addPipelineToTree(parentItem, newItem);
                    } else if (this._hasRequestedItemsAtPath(parentPath)) {
                        this._clearChildItems(parentItem);
                    }
                    return;
                }
            }
        }
    }

    public folderDeleted(deletedPath: string) {
        for (let i = 0; i < this.value.length; i++) {
            if (this.value[i].underlyingItem.data.value.folder && this.value[i].underlyingItem.data.value.path === deletedPath) {
                const itemToDelete = this.value[i];
                this.splice(itemToDelete.parentItem ? itemToDelete.parentItem.underlyingItem : undefined, [itemToDelete.underlyingItem], undefined);

                if (
                    itemToDelete.parentItem &&
                    (!itemToDelete.parentItem.underlyingItem.childItems || itemToDelete.parentItem.underlyingItem.childItems.length === 0)
                ) {
                    this.splice(itemToDelete.parentItem.underlyingItem, undefined, [
                        { items: [this._getEmptyFolderIndicator(itemToDelete.parentItem.underlyingItem.data.value.path)] }
                    ]);
                }

                return;
            }
        }
    }

    public pipelineRenamed(pipelineId: number, newPipelineName: string) {
        for (let i = 0; i < this.value.length; i++) {
            const treeItem = this.value[i].underlyingItem.data.value;
            const pipeline = treeItem.pipeline;
            if (pipeline !== undefined && pipeline.id === pipelineId) {
                pipeline.name = newPipelineName;
                this.value[i].underlyingItem.data.value = {
                    pipeline: pipeline,
                    isEmptyMarker: false,
                    isLoading: false,
                    linkProps: treeItem.linkProps,
                    name: newPipelineName,
                    path: treeItem.path
                };
                return;
            }
        }
    }

    public pipelineMoved(pipelineId: number, newPipelinePath: string, newPipelineName: string) {
        const oldPipelineItem = this._findPipelineById(pipelineId);
        if (oldPipelineItem == null) {
            return;
        }

        this._removeItem(oldPipelineItem);

        const newPipelineItem = this._createNewPipelineItem(oldPipelineItem, newPipelineName, newPipelinePath);

        this._addPathMapping(oldPipelineItem.underlyingItem.data.value.path, newPipelinePath);

        if (newPipelinePath === "" || newPipelinePath === this._rootPath) {
            // Pipeline is mounted directly at the root of the current view, just add it
            this.splice(undefined, undefined, [{ items: [newPipelineItem] }]);
        } else {
            const closestParent = this._findClosestParentFolder(newPipelinePath);

            if (!closestParent) {
                // There is no common directory prefix we could use in the current folder tree.
                // We're mounting only the first folder of the new pipeline path at the root.
                // If the path is longer, it is going to be recreated when the folder is first expanded.
                this._pipelineMovedToNewFolderAtRoot(newPipelineItem);
            } else if (closestParent.underlyingItem.data.value.path === newPipelinePath) {
                // Pipeline was moved to an existing path
                this._pipelineMovedToExistingFolder(closestParent.underlyingItem, newPipelineItem);
            } else if (closestParent.underlyingItem.expanded) {
                // There is a common directory prefix we could use in the current folder tree.
                // We can place the next folder in the new pipeline path under it.
                // The rest will be loaded once that folder is expanded.
                this._pipelineMovedToNewSubFolderOfExpandedFolder(closestParent.underlyingItem, newPipelineItem);
            } else {
                // There is a parent but it is collapsed so we just need to reload it so that the next time it is expanded we can see the new folder.
                this._reloadFolder(closestParent.underlyingItem);
                this._detailRequests[newPipelineItem.data.value.pipeline!.id] = LoadingStatus.NotRequested;
            }
        }
    }

    public pipelineDeleted(pipelineId: number) {
        for (let i = 0; i < this.value.length; i++) {
            const pipeline = this.value[i].underlyingItem.data.value.pipeline;
            if (pipeline !== undefined && pipeline.id === pipelineId) {
                const parent = this.value[i].parentItem;
                if (parent !== undefined) {
                    this.splice(parent.underlyingItem, [this.value[i].underlyingItem], undefined);
                } else {
                    this.splice(undefined, [this.value[i].underlyingItem], undefined);
                }
                break;
            }
        }
    }

    public setFilteredProvider(provider: FilteredPipelineTreeItemProvider | undefined) {
        this._filteredProvider = provider;
    }

    public getMappedPath(path: string): string {
        const mappedPath = this._pathMapping[path];
        return mappedPath !== undefined ? mappedPath : path;
    }

    private _getChildItems(parentItem?: ITreeItem<ObservableValue<IPipelineTreeItem>>) {
        let path = parentItem ? parentItem.data.value.path : this._rootPath;
        this._paths[path] = LoadingStatus.Requested;
        if (parentItem) {
            setTimeout(() => {
                if (this._paths[path] === LoadingStatus.Requested) {
                    let childItems = [
                        {
                            data: new ObservableValue<IPipelineTreeItem>({
                                name: "",
                                path: "",
                                isLoading: true,
                                isEmptyMarker: false
                            })
                        }
                    ];
                    this.splice(parentItem, undefined, [{ items: childItems }]);
                }
            }, 300);
        }

        this._source.getDataAsync("ms.vss-build-web.pipelines-data-provider", { path: path }, true).then(result => {
            this._onChildItemsAvailable(result as PipelineDataList, parentItem);
        });
    }

    private _getChildItemsOnPageLoad() {
        let path = this._rootPath;
        this._paths[path] = LoadingStatus.Available;
        let data = this._source.getData("ms.vss-build-web.pipelines-data-provider") as PipelineDataList;
        const treeItems = this._convertPipelinesDataToTreeItems(path, data);
        this.splice(undefined, undefined, [{ items: treeItems }]);
    }

    private _hasRequestedItemsAtPath(path: string) {
        return this._paths[path] && this._paths[path] !== LoadingStatus.NotRequested;
    }

    private _clearChildItems(parentItem: ITreeItem<ObservableValue<IPipelineTreeItem>>) {
        this._paths[parentItem.data.value.path] = LoadingStatus.NotRequested;
        if (parentItem.childItems) {
            parentItem.childItems.forEach((treeItem: ITreeItem<ObservableValue<IPipelineTreeItem>>) => {
                if (treeItem.data.value.pipeline) {
                    this._detailRequests[treeItem.data.value.pipeline.id] = LoadingStatus.NotRequested;
                } else if (treeItem.data.value.folder) {
                    this._clearChildItems(treeItem);
                }
            });
            this.splice(parentItem, parentItem.childItems);
        }
    }

    private _onChildItemsAvailable = (data: PipelineDataList, parentItem?: ITreeItem<ObservableValue<IPipelineTreeItem>>) => {
        let path = parentItem ? parentItem.data.value.path : this._rootPath;
        this._paths[path] = LoadingStatus.Available;
        if (parentItem) {
            if (parentItem.childItems) {
                this.splice(parentItem, parentItem.childItems);
            }
        }

        const treeItems = this._convertPipelinesDataToTreeItems(path, data);

        // If there are zero child items, insert an empty marker to have UI indicate that the folder is empty.
        if (parentItem && treeItems.length === 0) {
            this.splice(parentItem, undefined, [{ items: [this._getEmptyFolderIndicator(path)] }]);
        } else {
            this.splice(parentItem, undefined, [{ items: treeItems }]);
        }

        if (!parentItem) {
            this._loadingRoot.value = false;
        }
    };

    private _convertPipelinesDataToTreeItems(parentPath: string, pipelinesData: PipelineDataList): ITreeItem<ObservableValue<IPipelineTreeItem>>[] {
        let treeItems: ITreeItem<ObservableValue<IPipelineTreeItem>>[] = [];
        if (pipelinesData) {
            if (pipelinesData.folders) {
                pipelinesData.folders.forEach((folder: FolderData) => {
                    if (folder.path !== parentPath) {
                        const expanded = this._isExpanded(folder.path);
                        const treeItem = {
                            childItems: [],
                            expanded: expanded,
                            data: new ObservableValue<IPipelineTreeItem>({
                                name: folder.path.split("\\").reverse()[0],
                                path: folder.path,
                                isLoading: false,
                                isEmptyMarker: false,
                                folder: folder
                            })
                        } as ITreeItem<ObservableValue<IPipelineTreeItem>>;

                        treeItems.push(treeItem);

                        if (treeItem.expanded && !this._hasRequestedItemsAtPath(treeItem.data.value.path)) {
                            this._getChildItems(treeItem);
                        }
                    }
                });
            }
            if (pipelinesData.pipelines) {
                pipelinesData.pipelines.forEach((pipeline: PipelineData) => {
                    treeItems.push({
                        childItems: undefined,
                        data: new ObservableValue<IPipelineTreeItem>({
                            name: pipeline.name,
                            path: pipeline.path,
                            isLoading: false,
                            isEmptyMarker: false,
                            pipeline: pipeline,
                            linkProps: pipeline.linkProps
                        })
                    });
                });
            }
        }
        return treeItems;
    }

    private _fetchDetails = () => {
        // Reset to empty for next batch
        let batch = this._batchForUpdates;
        this._batchForUpdates = [];

        this._source.getDataAsync("ms.vss-build-web.pipelines-data-provider", { definitionIds: batch.join(",") }, true).then(result => {
            this._onLatestRunDetailsAvailable(result as PipelineDataList);
        });
    };

    private _onLatestRunDetailsAvailable = (data: PipelineDataList) => {
        if (data && data.pipelines) {
            data.pipelines.forEach((pipelineData: PipelineData) => {
                this.value.forEach((value: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
                    const pipelineTreeItem = value.underlyingItem.data.value;
                    const pipeline = pipelineTreeItem.pipeline;
                    if (pipeline && pipeline.id === pipelineData.id) {
                        this._detailRequests[pipeline.id] = LoadingStatus.Available;
                        pipeline.latestRun = pipelineData.latestRun;
                        value.underlyingItem.data.value = pipelineTreeItem;
                    }
                });
            });
        }
    };

    private _isExpanded = (path: string): boolean => {
        return this._expandedFolders.indexOf(path) > -1;
    };

    private _addPipelineToTree = (
        parentItem: ITreeItem<ObservableValue<IPipelineTreeItem>>,
        newItem: ITreeItem<ObservableValue<IPipelineTreeItem>>
    ): void => {
        // If the parent has the empty folder marker as its child, remove it when inserting the new folder
        // Otherwise, just insert the new folder alongside other children of the parent
        if (parentItem.childItems && parentItem.childItems.length === 1 && parentItem.childItems[0].data.value.isEmptyMarker) {
            this.splice(parentItem, [parentItem.childItems[0]], [{ items: [newItem] }]);
        } else {
            this.splice(parentItem, undefined, [{ items: [newItem] }]);
        }
    };

    private _buildUpdated = (payload: IBuildUpdatedPayload) => {
        let foundMatch = false;
        this.value.forEach((value: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
            const pipelineTreeItem = value.underlyingItem.data.value;
            const pipeline = pipelineTreeItem.pipeline;
            if (pipeline && pipeline.id == payload.definitionId && pipeline.latestRun && pipeline.latestRun.id == payload.buildId) {
                foundMatch = true;
            }
        });

        if (!foundMatch && this._filteredProvider) {
            foundMatch = this._filteredProvider.containsRun(payload.buildId);
        }

        if (foundMatch && this._runsWithUpdates.indexOf(payload.buildId) < 0) {
            this._runsWithUpdates.push(payload.buildId);
            this._getUpdatedRunsDebounced();
        }
    };

    private _updateStages = (payload: IStagesUpdatedPayload): void => {
        if (this.value && payload && payload.buildId) {
            let matchingPipelineTreeItem = this.value.find(treeItem =>
                this.matchTreeItemToBuildId(treeItem.underlyingItem.data.value.pipeline, payload.buildId)
            );

            if (!matchingPipelineTreeItem && this._filteredProvider) {
                matchingPipelineTreeItem = this._filteredProvider.value.find(treeItem =>
                    this.matchTreeItemToBuildId(treeItem.underlyingItem.data.value.pipeline, payload.buildId)
                );
            }

            let matchingRun = matchingPipelineTreeItem && matchingPipelineTreeItem.underlyingItem.data.value.pipeline!.latestRun;

            // only need to re-fetch run corresponding to stage update if current build status is
            // NotStarted or InProgress. NotStarted could also indicate that the pipeline
            // is pending on approval in this case
            if (
                matchingRun &&
                (matchingRun.status === BuildStatus.NotStarted || matchingRun.status === BuildStatus.InProgress) &&
                this._runsWithUpdates.indexOf(payload.buildId) < 0
            ) {
                this._runsWithUpdates.push(payload.buildId);
                this._getUpdatedRunsDebounced();
            }
        }
    };

    private matchTreeItemToBuildId(pipeline: PipelineData | undefined, buildId: number) {
        return pipeline && pipeline.latestRun && pipeline.latestRun.id === buildId;
    }

    private _getUpdatedRuns = () => {
        const runsToUpdate = this._runsWithUpdates;
        this._runsWithUpdates = [];

        if (runsToUpdate.length > 0) {
            this._source.getDataAsync("ms.vss-build-web.pipelines-data-provider", { runIds: runsToUpdate.join(",") }, true).then(result => {
                this._onLatestRunDetailsUpdated(result as PipelineDataList);
            });
        }
    };

    private _onLatestRunDetailsUpdated = (data: PipelineDataList) => {
        if (data && data.updatedRuns) {
            data.updatedRuns.forEach((runData: RunData) => {
                this.value.forEach((value: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
                    const pipelineTreeItem = value.underlyingItem.data.value;
                    const pipeline = pipelineTreeItem.pipeline;
                    if (pipeline && pipeline.latestRun && pipeline.latestRun.id === runData.id) {
                        // Preserve the commit message since it is not included when getting updated run status
                        const commitMessage = pipeline.latestRun.sourceVersionMessage;
                        pipeline.latestRun = runData;
                        pipeline.latestRun.sourceVersionMessage = commitMessage;
                        value.underlyingItem.data.value = pipelineTreeItem;
                    }
                });
            });

            if (this._filteredProvider) {
                this._filteredProvider.updateRuns(data.updatedRuns);
            }
        }
    };

    private _addPathMapping(originalPath: string, newPath: string) {
        // The path mapping allows for the UI to check against the right permissions
        // after renaming a folder or carry over the parent folder permissions when
        // adding a new folder.
        if (this._pathMapping[originalPath] !== undefined) {
            this._pathMapping[newPath] = this._pathMapping[originalPath];
        } else {
            this._pathMapping[newPath] = originalPath;
        }
        // reset original path mapping
        this._pathMapping[originalPath] = originalPath;
    }

    private _getEmptyFolderIndicator(path: string): ITreeItem<ObservableValue<IPipelineTreeItem>> {
        return {
            data: new ObservableValue<IPipelineTreeItem>({
                isEmptyMarker: true,
                isLoading: false,
                name: "",
                path: path
            })
        };
    }

    private _findPipelineById(pipelineId: number): ITreeItemEx<ObservableValue<IPipelineTreeItem>> | null {
        for (let i = 0; i < this.value.length; i++) {
            const item = this.value[i];
            const underlyingItem = item.underlyingItem;
            const underlyingItemValue = underlyingItem.data.value;
            const pipeline = underlyingItemValue.pipeline;
            if (pipeline !== undefined && pipeline.id === pipelineId) {
                return item;
            }
        }
        return null;
    }

    private _removeItem(oldPipelineItem: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) {
        const parent = oldPipelineItem.parentItem;
        if (parent !== undefined) {
            this.splice(parent.underlyingItem, [oldPipelineItem.underlyingItem], undefined);
            if (!parent.underlyingItem.childItems || parent.underlyingItem.childItems.length === 0) {
                this.splice(parent.underlyingItem, undefined, [{ items: [this._getEmptyFolderIndicator(parent.underlyingItem.data.value.path)] }]);
            }
        } else {
            this.splice(undefined, [oldPipelineItem.underlyingItem], undefined);
        }
    }

    private _createNewPipelineItem(
        oldPipelineItem: ITreeItemEx<ObservableValue<IPipelineTreeItem>>,
        newPipelineName: string,
        newPipelinePath: string
    ) {
        const treeItem = oldPipelineItem.underlyingItem.data.value;
        const pipeline = treeItem.pipeline!;
        const newPipelineItem = {
            data: new ObservableValue<IPipelineTreeItem>({
                pipeline: {
                    id: pipeline.id,
                    isDraft: pipeline.isDraft,
                    isFavorite: pipeline.isFavorite,
                    latestRun: pipeline.latestRun,
                    linkProps: pipeline.linkProps,
                    name: newPipelineName,
                    path: newPipelinePath,
                    processType: pipeline.processType,
                    queueStatus: pipeline.queueStatus,
                    uri: pipeline.uri
                },
                isEmptyMarker: false,
                isLoading: false,
                linkProps: treeItem.linkProps,
                name: newPipelineName,
                path: newPipelinePath
            })
        };
        return newPipelineItem;
    }

    private _findClosestParentFolder(path: string): ITreeItemEx<ObservableValue<IPipelineTreeItem>> | null {
        let closestParent: ITreeItemEx<ObservableValue<IPipelineTreeItem>> | null = null;

        for (let i = 0; i < this.value.length; i++) {
            const thisItem = this.value[i];
            if (!thisItem.underlyingItem.data.value.folder) {
                continue;
            }

            const parentItem = thisItem.underlyingItem;
            const thisFolderPath = parentItem.data.value.path;

            if (thisFolderPath === path) {
                return thisItem;
            } else {
                if (path.startsWith(thisFolderPath) && thisItem.depth > (closestParent ? closestParent.depth : -1)) {
                    closestParent = thisItem;
                }
            }
        }
        return closestParent;
    }

    private _pipelineMovedToExistingFolder(
        parentFolder: ITreeItem<ObservableValue<IPipelineTreeItem>>,
        newPipelineItem: ITreeItem<ObservableValue<IPipelineTreeItem>>
    ) {
        const pipeline = newPipelineItem.data.value.pipeline;
        if (!pipeline) {
            return;
        }
        if (parentFolder.expanded) {
            this._addPipelineToTree(parentFolder, newPipelineItem);
        } else if (this._hasRequestedItemsAtPath(pipeline.path)) {
            this._reloadFolder(parentFolder);
        }
        this._detailRequests[pipeline.id] = LoadingStatus.NotRequested;
    }

    private _pipelineMovedToNewSubFolderOfExpandedFolder(
        closestParentItem: ITreeItem<ObservableValue<IPipelineTreeItem>>,
        newPipelineItem: ITreeItem<ObservableValue<IPipelineTreeItem>>
    ) {
        const pipeline = newPipelineItem.data.value.pipeline;

        if (!pipeline) {
            return;
        }

        const closestParentPath = closestParentItem.data.value.path;
        const relativePath = this._removePrefix(pipeline.path, closestParentPath);
        const newPipelineFolders = relativePath.split("\\");
        const childFolderName = newPipelineFolders[1];
        const childFolderPath = closestParentPath + "\\" + childFolderName;

        const folderItem = this._createFolderItem(childFolderPath);

        this.splice(closestParentItem, undefined, [{ items: [folderItem] }]);

        const isFolderDirectParentOfPipeline = newPipelineFolders.length === 2;
        if (isFolderDirectParentOfPipeline) {
            // also add the pipeline itself to this folder
            this._addPipelineToTree(folderItem, newPipelineItem);
        }
    }

    private _pipelineMovedToNewFolderAtRoot(newPipelineItem: ITreeItem<ObservableValue<IPipelineTreeItem>>) {
        const pipeline = newPipelineItem.data.value.pipeline;

        if (!pipeline) {
            return;
        }

        const newPipelineFolders = pipeline.path.split("\\");
        const firstFolderName = newPipelineFolders[1];
        const firstFolderPath = "\\" + firstFolderName;
        const folderItem = this._createFolderItem(firstFolderPath);

        this.splice(undefined, undefined, [{ items: [folderItem] }]);

        const isFolderDirectParentOfPipeline = newPipelineFolders.length === 2;
        if (isFolderDirectParentOfPipeline) {
            // also add the pipeline itself to this folder
            this._addPipelineToTree(folderItem, newPipelineItem);
            this._detailRequests[pipeline.id] = LoadingStatus.NotRequested;
        } else {
            this._reloadFolder(folderItem);
        }
    }

    private _createFolderItem(folderPath: string): ITreeItem<ObservableValue<IPipelineTreeItem>> {
        const linkingService = this._pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const folderUrl = linkingService.getPipelinesFolderUrl(undefined, undefined, folderPath);
        const folderName = folderPath.split("\\").reverse()[0];

        return {
            data: new ObservableValue<IPipelineTreeItem>({
                name: folderName,
                path: folderPath,
                isLoading: false,
                isEmptyMarker: false,
                folder: { path: folderPath, name: folderName, linkProps: { href: folderUrl } } as FolderData
            }),
            childItems: []
        };
    }

    private _removePrefix(value: string, prefix: string) {
        return value.startsWith(prefix) ? value.slice(prefix.length) : value;
    }

    private _reloadFolder(folder: ITreeItem<ObservableValue<IPipelineTreeItem>>) {
        this._clearChildItems(folder);
        this._getChildItems(folder);
    }
}

export class FilteredPipelineTreeItemProvider extends TreeItemProvider<ObservableValue<IPipelineTreeItem>> {
    constructor(context: IVssComponentContext, rootFolder: string, searchResults: PipelineData[]) {
        super([]);

        let treeItems: ITreeItem<ObservableValue<IPipelineTreeItem>>[] = [];
        if (searchResults) {
            let folderItems: ITreeItem<ObservableValue<IPipelineTreeItem>>[] = [];
            let pipelineItems: ITreeItem<ObservableValue<IPipelineTreeItem>>[] = [];
            searchResults.forEach((pipeline: PipelineData) => {
                if (pipeline.path === rootFolder) {
                    pipelineItems.push({
                        childItems: undefined,
                        data: new ObservableValue<IPipelineTreeItem>({
                            name: pipeline.name,
                            path: pipeline.path,
                            isLoading: false,
                            isEmptyMarker: false,
                            pipeline: pipeline,
                            linkProps: pipeline.linkProps
                        })
                    });
                } else {
                    let subpath = pipeline.path;
                    if (subpath.startsWith(rootFolder)) {
                        subpath = subpath.substring(rootFolder.length);
                    }
                    if (!subpath.startsWith("\\")) {
                        subpath = "\\" + subpath;
                    }
                    let pathSegments = subpath.split("\\");
                    let rootFolderName = pathSegments[1];
                    let rootFolderPath = rootFolder === "\\" ? "\\" + rootFolderName : rootFolder + "\\" + rootFolderName;
                    const linkingService = context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
                    const folderUrl = linkingService.getPipelinesFolderUrl(undefined, undefined, pipeline.path);

                    let found = false;
                    folderItems.forEach((existingFolder: ITreeItem<ObservableValue<IPipelineTreeItem>>) => {
                        if (existingFolder.data.value.path === rootFolderPath) {
                            this._populateParentFolders(pipeline, existingFolder);
                            found = true;
                        }
                    });
                    if (!found) {
                        let newFolder = {
                            childItems: [],
                            expanded: true,
                            data: new ObservableValue<IPipelineTreeItem>({
                                name: rootFolderName,
                                path: rootFolderPath,
                                isLoading: false,
                                isEmptyMarker: false,
                                folder: {
                                    path: rootFolderPath,
                                    name: rootFolderName,
                                    linkProps: { href: folderUrl }
                                } as FolderData
                            })
                        };
                        folderItems.push(newFolder);
                        this._populateParentFolders(pipeline, newFolder);
                    }
                }
            });

            treeItems = folderItems.concat(pipelineItems);
        }

        this.splice(undefined, undefined, [{ items: treeItems }]);
    }

    public containsRun(runId: number): boolean {
        let foundMatch = false;

        this.value.forEach((value: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
            const pipelineTreeItem = value.underlyingItem.data.value;
            const pipeline = pipelineTreeItem.pipeline;
            if (pipeline && pipeline.latestRun && pipeline.latestRun.id == runId) {
                foundMatch = true;
            }
        });

        return foundMatch;
    }

    public updateRuns(updatedRuns: RunData[]) {
        if (updatedRuns) {
            updatedRuns.forEach((runData: RunData) => {
                this.value.forEach((value: ITreeItemEx<ObservableValue<IPipelineTreeItem>>) => {
                    const pipelineTreeItem = value.underlyingItem.data.value;
                    const pipeline = pipelineTreeItem.pipeline;
                    if (pipeline && pipeline.latestRun && pipeline.latestRun.id === runData.id) {
                        // Preserve the commit message since it is not included when getting updated run status
                        const commitMessage = pipeline.latestRun.sourceVersionMessage;
                        pipeline.latestRun = runData;
                        pipeline.latestRun.sourceVersionMessage = commitMessage;
                        value.underlyingItem.data.value = pipelineTreeItem;
                    }
                });
            });
        }
    }

    private _populateParentFolders(pipeline: PipelineData, folder: ITreeItem<ObservableValue<IPipelineTreeItem>>) {
        const fullPath = pipeline.path;
        if (folder.data.value.path === fullPath) {
            if (!folder.childItems) {
                folder.childItems = [];
            }
            folder.childItems.push({
                childItems: undefined,
                data: new ObservableValue<IPipelineTreeItem>({
                    name: pipeline.name,
                    path: pipeline.path,
                    isLoading: false,
                    isEmptyMarker: false,
                    pipeline: pipeline,
                    linkProps: pipeline.linkProps
                })
            });
        } else {
            const remainingPath = fullPath.substring(folder.data.value.path.length);
            let nextFolder = remainingPath.split("\\")[1];
            let nextFolderPath = folder.data.value.path + "\\" + nextFolder;
            let found: boolean = false;
            if (folder.childItems) {
                folder.childItems.forEach((childItem: ITreeItem<ObservableValue<IPipelineTreeItem>>) => {
                    if (childItem.data.value.folder && childItem.data.value.path === nextFolderPath) {
                        this._populateParentFolders(pipeline, childItem);
                        found = true;
                    }
                });
            }
            if (!found) {
                let newFolder = {
                    childItems: [],
                    expanded: true,
                    data: new ObservableValue<IPipelineTreeItem>({
                        name: nextFolder,
                        path: nextFolderPath,
                        isLoading: false,
                        isEmptyMarker: false,
                        folder: {
                            path: nextFolderPath,
                            name: nextFolder
                        } as FolderData
                    })
                };
                if (!folder.childItems) {
                    folder.childItems = [];
                }
                folder.childItems.push(newFolder);
                this._populateParentFolders(pipeline, newFolder);
            }
        }
    }
}
